import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { GoCardlessStatusBadge } from './GoCardlessStatusBadge'
import GoCardlessMatchingModal from './GoCardlessMatchingModal'
import { ExternalLink, CreditCard, Link2, Unlink, AlertCircle } from 'lucide-react'
import { unlinkGoCardlessMandate } from '../supabase-client'
import { toast } from 'sonner'

/**
 * GoCardless Mandate Section Component (Inline Format)
 *
 * Displays mandate information and mapping options for customers with
 * GoCardless (Direct Debit) as their payment method.
 *
 * **Inline Component** - No Card wrapper (parent handles layout)
 *
 * Features:
 * - Link to GoCardless mandate via enhanced modal
 * - Display mandate details for mapped customers
 * - Unlink/change mandate functionality
 * - Integration with gocardless_customer_mappings table
 *
 * @param {Object} props
 * @param {string|null} props.mandateId - GoCardless mandate ID (denormalized from customers table)
 * @param {string|null} props.mandateStatus - Current mandate status
 * @param {string|null} props.customerId - GoCardless customer ID
 * @param {Object} props.odinCustomer - Full Odin customer object (required for linking)
 * @param {Function} props.onMappingChange - Callback when mapping changes (to refresh parent data)
 * @param {Function} props.onLinkSuccess - Callback after successful linking (for PaymentIntegrationModal)
 * @returns {JSX.Element} Inline mandate section
 */
export function GoCardlessMandateSection({
  mandateId,
  mandateStatus,
  customerId,
  odinCustomer,
  onMappingChange,
  onLinkSuccess
}) {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false)
  const [isUnlinking, setIsUnlinking] = useState(false)

  const hasMandateLinked = !!mandateId

  /**
   * Handle successful link/change
   */
  const handleLinkSuccess = () => {
    setIsLinkModalOpen(false)
    if (onMappingChange) {
      onMappingChange() // Refresh parent data
    }
    if (onLinkSuccess) {
      onLinkSuccess() // Notify PaymentIntegrationModal
    }
    toast.success('Mandate linked successfully')
  }

  /**
   * Handle unlink
   */
  const handleUnlink = async () => {
    if (!odinCustomer?.id) {
      toast.error('Customer ID not found')
      return
    }

    const confirmed = window.confirm(
      `Are you sure you want to unlink this GoCardless mandate?\n\nThis will remove the Direct Debit payment method association for ${odinCustomer.name || odinCustomer.companyName}.`
    )

    if (!confirmed) return

    setIsUnlinking(true)

    try {
      await unlinkGoCardlessMandate(odinCustomer.id)

      toast.success('Mandate unlinked', {
        description: 'Customer has been unlinked from GoCardless mandate'
      })

      if (onMappingChange) {
        onMappingChange() // Refresh parent data
      }
    } catch (error) {
      console.error('[GoCardlessMandateSection] Unlink error:', error)
      toast.error('Failed to unlink mandate', {
        description: error.message
      })
    } finally {
      setIsUnlinking(false)
    }
  }

  if (!odinCustomer) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
        <AlertCircle className="h-4 w-4" />
        Save the customer first to link a GoCardless mandate
      </div>
    )
  }

  return (
    <>
      <div className="space-y-4">
          {hasMandateLinked ? (
            // Display existing mandate information
            <>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <div className="mt-1">
                    <GoCardlessStatusBadge type="mandate" status={mandateStatus} />
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open('https://manage.gocardless.com', '_blank')}
                  className="gap-2"
                >
                  View in GoCardless
                  <ExternalLink className="size-4" />
                </Button>
              </div>

              <div className="border-t pt-4">
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Mandate ID:</span>
                    <span className="font-mono font-medium">{mandateId}</span>
                  </div>
                  {customerId && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Customer ID:</span>
                      <span className="font-mono font-medium">{customerId}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="border-t pt-4 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsLinkModalOpen(true)}
                  className="flex-1"
                >
                  Change Mandate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleUnlink}
                  disabled={isUnlinking}
                  className="flex-1"
                >
                  {isUnlinking ? (
                    <>Unlinking...</>
                  ) : (
                    <>
                      <Unlink className="size-4 mr-2" />
                      Unlink
                    </>
                  )}
                </Button>
              </div>

              {(mandateStatus === 'cancelled' || mandateStatus === 'expired' || mandateStatus === 'failed') && (
                <div className="border-t pt-4">
                  <div className="text-sm text-amber-700 bg-amber-50 p-3 rounded-md border border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-medium">Mandate No Longer Active</p>
                        <p className="text-xs mt-1">
                          This mandate cannot be used for payments. Link a new active mandate to collect payments.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            // Display setup prompt for new mandate
            <div className="text-center py-6">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
                  <CreditCard className="size-8 text-green-600" />
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">
                  No Mandate Linked
                </h4>
                <p className="text-sm text-gray-600 max-w-md mx-auto">
                  This customer has GoCardless selected as their payment method but hasn't been linked to a
                  GoCardless mandate yet.
                </p>
              </div>
              <Button
                type="button"
                variant="default"
                onClick={() => setIsLinkModalOpen(true)}
                className="gap-2"
              >
                <Link2 className="size-4" />
                Link GoCardless Mandate
              </Button>
              <p className="text-xs text-gray-500 mt-3">
                Link to an existing GoCardless customer and mandate
              </p>
            </div>
          )}
      </div>

      {/* Link/Change Modal */}
      <GoCardlessMatchingModal
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        customer={{
          id: odinCustomer.id,
          name: odinCustomer.name || odinCustomer.companyName,
          email: odinCustomer.email
        }}
        onSuccess={handleLinkSuccess}
      />
    </>
  )
}

export default GoCardlessMandateSection
